import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, Search, Grid, List, Download, Share2, Heart, ArrowUp } from 'lucide-react';
import SectionTitle from './SectionTitle';
import SEO from './SEO';
import config from '../utils/config';


const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'masonry'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'
  const [favorites, setFavorites] = useState([]);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);

  const categories = [
    { id: 'all', name: 'सभी फोटो', icon: '📷' },
    { id: 'events', name: 'कार्यक्रम', icon: '🎉' },
    { id: 'service', name: 'सेवा कार्य', icon: '🤝' },
    { id: 'community', name: 'सामुदायिक', icon: '👥' },
    { id: 'awards', name: 'सम्मान एवं पुरस्कार', icon: '🏆' }
  ];

  // Fetch gallery data from backend
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/gallery`);
        if (!response.ok) {
          throw new Error('Failed to fetch gallery data');
        }
        const data = await response.json();
        setGalleryData(data);
      } catch (err) {
        console.error('Error fetching gallery data:', err);
        setError('गैलरी डेटा लोड करने में समस्या आई है। कृपया पुनः प्रयास करें।');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  // Filter and sort images
  const getFilteredAndSortedImages = () => {
    let filtered = galleryData;

    // Filter by category
    if (currentCategory !== 'all') {
      filtered = filtered.filter(img => img.category === currentCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(img => 
        img.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        img.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort images
    switch (sortBy) {
      case 'newest':
        filtered = [...filtered].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'oldest':
        filtered = [...filtered].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredImages = getFilteredAndSortedImages();

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % filteredImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? filteredImages.length - 1 : selectedImage - 1);
    }
  };

  const toggleFavorite = (imageId) => {
    setFavorites(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const downloadImage = (imageUrl, title) => {
    const link = document.createElement('a');
    link.href = `${config.url}${imageUrl}`;
    link.download = `${title || 'image'}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareImage = async (imageUrl, title) => {
    const shareUrl = `${window.location.origin}/gallery?image=${encodeURIComponent(imageUrl)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'Gallery Image',
          text: 'Check out this amazing photo from our gallery!',
          url: shareUrl
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const handleImageLoad = (imageId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  const handleImageError = (imageId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageId]: false
    }));
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage !== null) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowRight') {
          nextImage();
        } else if (e.key === 'ArrowLeft') {
          prevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, filteredImages.length]);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-lg text-gray-600">गैलरी लोड हो रही है...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
              <p className="text-lg">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
              >
                पुनः प्रयास करें
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO 
        title="फोटो गैलरी"
        description="सीधी सोच सीधी बात की फोटो गैलरी। विभिन्न कार्यक्रमों, सेवा कार्यों और सामुदायिक गतिविधियों की तस्वीरें देखें।"
        keywords="फोटो गैलरी, कार्यक्रम, सेवा कार्य, सामुदायिक गतिविधियां, सम्मान, पुरस्कार, सीधी सोच सीधी बात"
        url="/gallery"
      />
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-16">
          <SectionTitle>फोटो गैलरी</SectionTitle>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            विभिन्न कार्यक्रमों और उत्सवों की यादें - हर तस्वीर एक कहानी कहती है।
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-red-600 mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="फोटो खोजें..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white shadow-sm text-orange-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('masonry')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'masonry' 
                    ? 'bg-white shadow-sm text-orange-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            >
              <option value="newest">नवीनतम पहले</option>
              <option value="oldest">पुराने पहले</option>
              <option value="name">नाम के अनुसार</option>
            </select>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setCurrentCategory(category.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 border-2 flex items-center gap-2 ${
                currentCategory === category.id
                  ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg border-orange-600 transform scale-105'
                  : 'border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white hover:scale-105'
              }`}
            >
              <span>{category.icon}</span>
              {category.name}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="text-center mb-8">
          <p className="text-gray-600">
            {filteredImages.length} फोटो मिले {currentCategory !== 'all' ? `(${categories.find(c => c.id === currentCategory)?.name})` : ''}
          </p>
        </div>

        {/* Gallery Grid */}
        {filteredImages.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">📷</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई फोटो नहीं मिला</h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `"${searchTerm}" के लिए कोई परिणाम नहीं मिला।`
                  : currentCategory === 'all' 
                  ? 'अभी तक कोई फोटो अपलोड नहीं किया गया है।' 
                  : `इस श्रेणी में कोई फोटो नहीं है।`
                }
              </p>
            </div>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
              : 'columns-1 sm:columns-2 md:columns-3 lg:columns-4'
          }`}>
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className={`group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 rounded-2xl border-0 bg-white ${
                  viewMode === 'masonry' ? 'break-inside-avoid mb-6' : ''
                }`}
                onClick={() => openLightbox(index)}
              >
                <div className="p-0 relative">
                  <img
                    src={`${config.url}${image.image_url}`}
                    alt={image.title}
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${
                      viewMode === 'grid' ? 'h-64 sm:h-72 md:h-64' : 'h-auto'
                    } ${!imageLoadingStates[image.id] ? 'animate-pulse bg-gray-200' : ''}`}
                    onLoad={() => handleImageLoad(image.id)}
                    onError={(e) => {
                      handleImageError(image.id);
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                  />
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-between p-4">
                    {/* Top actions */}
                    <div className="flex justify-between items-start">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(image.id);
                        }}
                        className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
                      >
                        <Heart 
                          className={`w-5 h-5 ${
                            favorites.includes(image.id) ? 'fill-red-500 text-red-500' : 'text-white'
                          }`} 
                        />
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadImage(image.image_url, image.title);
                          }}
                          className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
                        >
                          <Download className="w-5 h-5 text-white" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            shareImage(image.image_url, image.title);
                          }}
                          className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
                        >
                          <Share2 className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>

                    {/* Bottom content */}
                    <div className="text-white text-center transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="text-lg font-bold mb-2">{image.title}</h3>
                      {image.description && (
                        <p className="text-sm text-gray-200 mb-2 line-clamp-2">{image.description}</p>
                      )}
                      <div className="w-8 h-0.5 bg-orange-400 mx-auto"></div>
                    </div>
                  </div>

                  {/* Category badge */}
                  {image.category && (
                    <div className="absolute top-4 left-4 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {categories.find(c => c.id === image.category)?.name || image.category}
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        {selectedImage !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-2 backdrop-blur-sm"
            >
              <X className="w-8 h-8" />
            </button>
            
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-3 backdrop-blur-sm"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-3 backdrop-blur-sm"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="max-w-5xl max-h-full">
              <img
                src={`${config.url}${filteredImages[selectedImage].image_url}`}
                alt={filteredImages[selectedImage].title}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                }}
              />
              <div className="text-white text-center mt-6 bg-black/50 rounded-lg p-4 backdrop-blur-sm">
                <h3 className="text-2xl font-semibold mb-2">
                  {filteredImages[selectedImage].title}
                </h3>
                {filteredImages[selectedImage].description && (
                  <p className="text-gray-200 max-w-2xl mx-auto">
                    {filteredImages[selectedImage].description}
                  </p>
                )}
                <div className="flex justify-center items-center gap-4 mt-4 text-sm text-gray-300">
                  <span>फोटो {selectedImage + 1} / {filteredImages.length}</span>
                  {filteredImages[selectedImage].category && (
                    <span className="bg-orange-600 px-3 py-1 rounded-full">
                      {categories.find(c => c.id === filteredImages[selectedImage].category)?.name || filteredImages[selectedImage].category}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-gradient-to-r from-orange-600 to-red-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-40"
          >
            <ArrowUp className="w-6 h-6" />
          </button>
        )}
      </div>
    </section>
    </>
  );
};

export default GalleryPage; 